/**
 * 创建带重试机制的动态导入函数
 */
export const createRetryableImport =
  <T>(importFn: () => Promise<T>, maxRetries = 3) =>
  () =>
    retryDynamicImport(importFn, maxRetries, 1000, true);

/**
 * 重试动态导入
 * @param importFn 动态导入函数
 * @param maxRetries 最大重试次数
 * @param delay 重试延迟时间（毫秒）
 * @param clearCache 是否清除缓存
 */
export const retryDynamicImport = async <T>(
  importFn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
  clearCache = false,
): Promise<T> => {
  let lastError: Error | undefined;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await importFn();
    } catch (error) {
      lastError = error as Error;

      if (
        error instanceof Error &&
        (error.name === 'ChunkLoadError' ||
          error.message.includes('Loading chunk') ||
          error.message.includes('ChunkLoadError'))
      ) {
        if (i < maxRetries) {
          clearCache && (await handleChunkError(error).then());
          await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
          continue;
        }
      }

      throw error;
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error('Unknown error occurred during dynamic import');
};

/**
 * 处理 webpack chunk 加载错误
 */
export const handleChunkError = async (error: Error) => {
  if (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('ChunkLoadError')
  ) {
    if (typeof window !== 'undefined' && 'caches' in window) {
      const cacheNames = await caches.keys();
      const nextCaches = cacheNames.filter(
        (name) => name.includes('next-static') || name.includes('webpack'),
      );
      await Promise.all(
        nextCaches.map((cacheName) => caches.delete(cacheName)),
      );
    }
  }
};
